import React, { useEffect, useState } from 'react';
import CommandPalette, { useHandleOpenCommandPalette } from 'react-cmdk';
import styled from 'styled-components';
// import { Loading } from 'components/atoms/loading';
import { Tag } from 'components/atoms/tag';
import { useTranslation } from 'hooks/use-translation';
import { getRandomColor } from 'utils/get-random-color';
import { useCmdkEntries } from './entries';
import { CmdkFooter } from './footer';
import { GroupToList } from './group';
import { CommandGroup } from './types';

// const LoadingWrapper = styled.div`
//   display: flex;
//   align-items: center;
//   justify-content: center;
// `;

const TabsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  padding: var(--spacing-01) var(--spacing-03);
`;

const CommandPaletteWrapper = styled.div<{ isPadded: boolean }>`
  height: 450px;
  .flex-1.overflow-y-auto.focus:outline-none.p-2.space-y-4 {
    padding: ${(props) => (props.isPadded ? '0.5rem' : '0')};
  }

  h4.px-3\.5.text-gray-500.text-sm.font-medium {
    margin-top: var(--spacing-03);
    margin-bottom: var(--spacing-03);

    @media (prefers-color-scheme: dark) {
      color: var(--white-100) !important;
    }
  }
`;

const CommandBarContent: React.FC<{
  entries: Array<CommandGroup>;
  filterText: string;
}> = ({ entries, filterText }) => {
  useEffect(() => {
    // to hide autofill popup
    document
      .getElementById('command-palette-search-input')
      ?.setAttribute('autocomplete', 'off');
  }, []);

  if (entries.length === 1) {
    return (
      <>
        {entries.map((group) => (
          <GroupToList group={group} filterText={filterText} renderHeader />
        ))}
      </>
    );
  }

  const tabs = entries.filter((e) => e.tabbable);

  return (
    <>
      <TabsWrapper>
        {tabs.length > 1
          ? tabs.map((e) => (
              <Tag color={getRandomColor(e.label)}> {e.label} </Tag>
            ))
          : null}
      </TabsWrapper>
      {entries.map((group) => (
        <GroupToList group={group} filterText={filterText} renderHeader />
      ))}
    </>
  );
};

const MemoisedGroups: React.FC<{
  debouncedSearch: string;
  entries: CommandGroup[];
}> = React.memo(
  ({ debouncedSearch, entries }) => (
    <CommandBarContent
      key={debouncedSearch}
      entries={entries}
      filterText={debouncedSearch}
    />
  ),
  (p, n) => p.debouncedSearch === n.debouncedSearch,
);

export const CommandBar: React.FC = () => {
  const [isOpen, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [search]);

  const entries = useCmdkEntries();
  const placeHolderForCmdk = useTranslation('cmdk.search.placeholder');

  useHandleOpenCommandPalette(setOpen);

  return (
    <CommandPalette
      isOpen={isOpen}
      onChangeOpen={setOpen}
      onChangeSearch={setSearch}
      search={search}
      placeholder={placeHolderForCmdk}
      footer={<CmdkFooter />}
    >
      <CommandPaletteWrapper isPadded={!!search}>
        <MemoisedGroups debouncedSearch={debouncedSearch} entries={entries} />
      </CommandPaletteWrapper>
    </CommandPalette>
  );
};
